import React from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import classNames from "classnames";

import { convertGain, getMeasurementUnits } from "utils/displayMeasurements";
import { detectZoneChanges, getZoneStatusText } from "utils/singleZoneCooling";
import { selectUpgradePackagesSortedArray } from "features/Model/Upgrades/_ducks/selectors";

import FixedBanner from "components/FixedBanner";
import PDF_Export_Button from "../PDF_Export/Button";
import GainsBreakdown from "./GainsBreakdown";
import ZoneRoomBreakdown from "./ZoneRoomBreakdown";
import VentilationBreakdown from "./VentilationBreakdown";
import { Stat } from "features/Model/Review/Results/General";

import classes from "./styles.module.scss";
import resultsClasses from "features/Model/Review/Results/style.module.scss";

const ZoneResults = ({
    zoneId = "",
    zoneData = {},
    displayUnits = "Metric",
    results = {},
    hasErrors = false,
    hasWarnings = false,
    hasGlobalChanges = false,
}) => {
    const { data: resultsData = {}, upgradePackageId = "", updatedAt = "" } = results;

    const {
        selectedStorey = "",
        selectedRooms = [],
        selectedWalls,
        roomLabels = [],
        exteriorComponents = [],
        interiorComponents = [],
        area = 0,
        volume = 0,
        totalSensHeatGain = 0,
        totalSensLatHeatGain = 0,
    } = resultsData;

    const changesDetected = hasGlobalChanges || detectZoneChanges(zoneData, resultsData);
    const hasResults = results && !isEmpty(results);

    const { gainDisplayUnits } = getMeasurementUnits(displayUnits);

    const upgradePackages = useSelector(selectUpgradePackagesSortedArray) || [];
    const upgradePackageName =
        upgradePackages.find(({ id }) => id === upgradePackageId)?.name ||
        (upgradePackageId === "base" ? "Base" : upgradePackageId);

    return (
        <div className={classes.zoneResultsContainer}>
            <div
                className={classNames(classes.bannersContainer, {
                    [classes.noMarginBottom]: !hasResults || (!hasErrors && !hasWarnings && !changesDetected)
                })}
            >
                {changesDetected && hasResults && (
                    <FixedBanner className={`${classes.banner} ${classes.infoBanner}`} type="changes">
                        <p>{getZoneStatusText("changes")}</p>
                    </FixedBanner>
                )}
                {!hasResults && (
                    <FixedBanner className={`${classes.banner} ${classes.noResultsBanner}`} type="noResults">
                        <p>{getZoneStatusText("noResults")}</p>
                    </FixedBanner>
                )}
                {hasErrors ? (
                    <FixedBanner className={`${classes.banner} ${classes.errorBanner}`} type="error">
                        <p>{getZoneStatusText("error")}</p>
                    </FixedBanner>
                ) : hasWarnings && (
                    <FixedBanner className={`${classes.banner} ${classes.warningBanner}`} type="warning">
                        <p>{getZoneStatusText("warning")}</p>
                    </FixedBanner>
                )}
            </div>
            {hasResults && (
                <div className={classes.viewResults}>
                    <div className={classes.resultsHeader}>
                        <h3>Cooling Zone Analysis</h3>
                        <PDF_Export_Button
                            results={resultsData}
                            displayUnits={displayUnits}
                            disabled={hasErrors}
                        />
                    </div>
                    <div className={classes.summaryRow}>
                        <div className={classNames(classes.statsGrid, resultsClasses.row, resultsClasses.totals)}>
                            <Stat
                                label="Design Heat Gain (Sensible + Latent)"
                                value={convertGain(totalSensLatHeatGain, gainDisplayUnits) || "N/A"}
                                unit={gainDisplayUnits}
                                decimals={0}
                                big
                            />
                            <Stat
                                label="Design Heat Gain (Sensible)"
                                value={convertGain(totalSensHeatGain, gainDisplayUnits) || "N/A"}
                                unit={gainDisplayUnits}
                                decimals={0}
                                big
                            />
                        </div>
                        <div className={classes.zoneDetailsRow}>
                            <div className={classes.resultItem}>
                                <p className={classes.resultItemTitle}>Upgrade Package:</p>
                                <p>{upgradePackageName}</p>
                            </div>
                            <div className={classes.resultItem}>
                                <p className={classes.resultItemTitle}>Last run:</p>
                                <p>{moment(updatedAt).format("Do MMM YYYY, hh:mm A")}</p>
                            </div>
                        </div>
                    </div>
                    <GainsBreakdown results={resultsData} units={gainDisplayUnits} />
                    <ZoneRoomBreakdown
                        selectedStorey={selectedStorey}
                        selectedRooms={selectedRooms}
                        roomLabels={roomLabels}
                        exteriorComponents={exteriorComponents}
                        interiorComponents={interiorComponents}
                    />
                    <VentilationBreakdown results={resultsData} displayUnits={displayUnits} />
                </div>
            )}
        </div>
    );
};

export default ZoneResults;
